import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../../config/config";
import handleCSRF from "../../../config/CSRF";
import { toast } from "react-toastify";
import { languageHelper } from "../../../config/helperFunctions";

function AddOnForm() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  
  const { id } = useParams();
  const [ingredientList, setIngredientList] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(id ? true : false);

  const navigate = useNavigate();

  const handleIngredientSelect = (selectedOptions) => {
    setSelectedIngredients((prevSelectedIngredients) => {
      const newSelectedIngredients = selectedOptions.map((option) => {
        const existing = prevSelectedIngredients.find(
          (ing) => ing.value === option.value
        );
        return existing || { ...option, consumption: "" }; // Keep existing consumption or initialize
      });
      return newSelectedIngredients;
    });
  };

  const handleConsumptionChange = (index, value) => {
    setSelectedIngredients((prevIngredients) => {
      const updatedIngredients = [...prevIngredients];
      updatedIngredients[index].consumption = value;
      return updatedIngredients;
    });
  };

  const consumptionComponent = (unit, index) => (
    <div className="flex" key={index}>
      <input
        type="text"
        placeholder="Consumption"
        value={selectedIngredients[index]?.consumption || ""}
        onChange={(e) => handleConsumptionChange(index, e.target.value)}
        className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline"
      />
      <span className="text-sm text-d-bg-text py-3 mx-2">{unit}</span>
    </div>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const postData = (csrfToken) => {
      if (id) {
        axios
          .put(
            `${apiKey}/api/rms/admin/modifiers/${id}`,
            {
              name,
              price,
              ingredients: selectedIngredients,
              description,
              user_id: user?.user.id,
              outlet_id: user?.user.outlet_id,
            },
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          )
          .then((res) => {
            toast.success("Success", {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            });
            navigate(-1);
          })
          .catch((err) => {
            toast.error(err.response.data.message, {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            });
          })
          .finally(() => setLoading(false));
      } else {
        axios
          .post(
            `${apiKey}/api/rms/admin/modifiers`,
            {
              name,
              price,
              ingredients: selectedIngredients,
              description,
              user_id: user?.user.id,
              outlet_id: user?.user.outlet_id,
            },
            {
              headers: {
                Authorization: `Bearer ${user?.token}`,
              },
            }
          )
          .then((res) => {
            toast.success("Success", {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            });
            navigate(-1);
          })
          .catch((err) => {
            toast.error(err.response.data.message, {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
            });
          })
          .finally(() => setLoading(false));
      }
    };
    handleCSRF(postData);
  };

  useEffect(() => {
    const fetchIngredients = (csrfToken) => {
      axios
        .get(`${apiKey}/api/rms/admin/ingredients`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        })
        .then((res) => {
          const mappedOptions = res.data.map((item) => ({
            value: item.id,
            ingredient_id: item.id,
            label: item.name,
            unit: item.unit_name,
          }));
          setIngredientList(mappedOptions);
        })
        .catch((err) => {
          console.log(err.response.data.message);
        });
    };

    handleCSRF(fetchIngredients);

    if (id) {
      const fetchData = () => {
        axios
          .get(`${apiKey}/api/rms/admin/modifiers/${id}`, {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          })
          .then((res) => {
            setName(res.data.name);
            setPrice(res.data.price);
            setDescription(res.data.description);
            const mappedIng = res.data.modifier_ingredients.map((ing) => ({
              ingredient_id: ing.ingredient_id,
              consumption: ing.consumption,
              value: ing.ingredient_id,
              label: ing.ingredient?.name,
            }));
            setSelectedIngredients(mappedIng);
          })
          .catch((err) => {
            console.log(err.response.data.message);
          })
          .finally(() => {
            setPageLoading(false);
          });
      };
      handleCSRF(fetchData);
    }
  }, [id, user?.token]);

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-d-bg-text text-2xl">{languageHelper("Add Modifier")}</h1>
          </div>
        </div>
        <div className="w-full">
          <form
            action=""
            className="w-full"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full">
              <label htmlFor="" className="w-full lg:w-1/2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Name")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder={languageHelper("Name")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </label>
              <label htmlFor="" className="w-full lg:w-1/2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Price")}
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder={languageHelper("Price")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
              </label>
            </div>
            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full mt-3">
              <label htmlFor="" className="w-full lg:w-1/2">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  {languageHelper("Ingredient Consumptions")}
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    className=" placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    placeholder={languageHelper("Select")}
                    isMulti
                    options={ingredientList}
                    value={selectedIngredients}
                    onChange={handleIngredientSelect}
                  />
                </div>
              </label>
            </div>

            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left rtl:text-right">
                <thead className="uppercase font-medium">
                  <tr>
                    <th className="px-6 py-3">{languageHelper("SN")}</th>
                    <th className="px-6 py-3">{languageHelper("Ingredient")}</th>
                    <th className="px-6 py-3">{languageHelper("Consumption")}</th>
                    {/* <th className="px-6 py-3">Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {selectedIngredients.map((ingredient, index) => (
                    <tr key={index}>
                      <td className="px-6 py-1">{index + 1}</td>
                      <td className="px-6 py-1">{ingredient.label}</td>
                      <td className="px-6 py-1">
                        {consumptionComponent(ingredient.unit, index)}
                      </td>
                      {/* <td className="px-6 py-1">
                        <button
                          type="button"
                          className="rounded-full border border-[#7b20c7] w-fit p-1"
                          onClick={() => {
                            const updatedIngredients = [...selectedIngredients];
                            updatedIngredients.splice(index, 1);
                            setSelectedIngredients(updatedIngredients);
                          }}
                        >
                          <FaTrash className="fill-[#7B20C7] w-4" />
                        </button>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full mt-2">
              <label htmlFor="" className="w-full lg:w-1/2">
                <span className="text-d-bg-text text-sm mb-2 relative ">
                  {languageHelper("Description")}
                </span>
                <div className="mb-2 pt-0">
                  <textarea
                    rows={3}
                    type="text"
                    placeholder={languageHelper("Enter")}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </label>
            </div>

            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
                onClick={handleSubmit}
              >
                {loading && (
                  <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50">
                    <img
                      className="w-4 mx-auto"
                      src="https://i.gifer.com/ZKZx.gif"
                      alt=""
                    />
                  </span>
                )}
                {languageHelper("Submit") || "Submit"}
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                {languageHelper("Back") || "Back"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AddOnForm;
